<template>
  <v-dialog
    v-model="opened"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    width="500"
    @keydown.esc="closeDialog()"
  >
    <v-card v-if="!isEmpty(selectedEntityIds)">
      <v-card-title class="title pb-0">
        Notifications for {{ findEntityName(selectedEntityIds[0]) }}
        <v-tooltip bottom v-if="selectedEntityIds.length > 1">
          <span slot="activator" class="primary--text ml-2">
            (+{{ selectedEntityIds.length - 1 }} others)</span
          >
          <div
            v-for="(entityId, index) in selectedEntityIds"
            v-if="index !== 0"
          >
            {{ findEntityName(entityId) }}
          </div>
        </v-tooltip>
      </v-card-title>
      <v-card-text class="subheading darkgrey--text">
        <v-alert
          v-if="currentUserIsAssignedAdvisor"
          :value="true"
          outline
          block
          :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
          color="primary"
          class="text-xs-left"
        >
          You cannot disable notifications for clients and entities you are
          assigned to.
        </v-alert>
        <v-layout column class="text-xs-left pa-2">
          <v-layout align-center class="mb-4">
            <v-flex xs11 class="pr-4"
              >Information-related
              <p class="caption">
                Get notifications when client information is updated by other
                users.
              </p></v-flex
            >
            <v-spacer />
            <v-flex>
              <v-switch
                :label="
                  notificationSubscription.information_related ? 'On' : 'Off'
                "
                :disabled="
                  notificationSubscription.information_related &&
                    currentUserIsAssignedAdvisor
                "
                hide-details
                class="pt-0 mt-0"
                v-model="notificationSubscription.information_related"
              />
            </v-flex>
          </v-layout>
          <v-layout align-center class="mb-4">
            <v-flex xs11 class="pr-4"
              >Document-related
              <p class="caption">
                Get notifications when documents are uploaded via Document
                Folder (outside of questionnaire and conversation).
              </p></v-flex
            >
            <v-spacer />
            <v-flex>
              <v-switch
                :label="
                  notificationSubscription.document_related ? 'On' : 'Off'
                "
                :disabled="
                  notificationSubscription.document_related &&
                    currentUserIsAssignedAdvisor
                "
                hide-details
                class="pt-0 mt-0"
                v-model="notificationSubscription.document_related"
              />
            </v-flex>
          </v-layout>
          <v-layout align-center class="mb-4">
            <v-flex xs11 class="pr-4"
              >Questionnaire-related
              <p class="caption">
                Get notifications when a questionnaire is submitted.
              </p></v-flex
            >
            <v-spacer />
            <v-flex>
              <v-switch
                :label="
                  notificationSubscription.questionnaire_related ? 'On' : 'Off'
                "
                :disabled="
                  notificationSubscription.questionnaire_related &&
                    currentUserIsAssignedAdvisor
                "
                hide-details
                class="pt-0 mt-0"
                v-model="notificationSubscription.questionnaire_related"
              />
            </v-flex>
          </v-layout>
          <v-layout
            v-if="questionnaireDocumentNotificationEnabled"
            align-center
            class="mb-4"
          >
            <v-flex xs11 class="pr-4"
              >Questionnaire-Documents-related
              <p class="caption">
                Get notifications when documents are uploaded via
                questionnaires.
              </p></v-flex
            >
            <v-spacer />
            <v-flex>
              <v-switch
                :label="
                  notificationSubscription.questionnaire_document_related
                    ? 'On'
                    : 'Off'
                "
                hide-details
                class="pt-0 mt-0"
                v-model="
                  notificationSubscription.questionnaire_document_related
                "
                :disabled="
                  notificationSubscription.questionnaire_document_related &&
                    currentUserIsAssignedAdvisor
                "
              />
            </v-flex>
          </v-layout>
        </v-layout>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          class="primary text-capitalize"
          @click="saveNotificationSubscription()"
        >
          Save
        </v-btn>
        <v-btn
          class="error--text text-capitalize"
          outline
          @click="closeDialog()"
        >
          cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import API from '@/api';
import { mapState, mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';

export default {
  name: 'NotificationSubscriptionDialog',
  mixins: [FirmPreferenceMixins],
  props: {
    selectedEntityIds: {
      type: Array,
      default: [],
    },
    opened: {
      required: true,
      type: Boolean,
    },
  },

  data() {
    return {
      notificationSubscription: {
        document_related: false,
        questionnaire_related: false,
        information_related: false,
        questionnaire_document_related: false,
      },
    };
  },

  computed: {
    ...mapState('companies', ['companies', 'loadingCompanies']),
    ...mapGetters({
      currentUser: 'user',
    }),
    currentUserIsAssignedAdvisor() {
      let companies = this.companies.filter(d =>
        this.selectedEntityIds.includes(d.id),
      );
      return companies
        .map(c => c.user.assigned_advisor_id)
        .some(a => a === this.currentUser.id);
    },
  },

  methods: {
    isEmpty,
    findEntityName(entityId) {
      let company = this.companies.find(d => d.id === entityId);
      if (company) {
        return company.name;
      }
    },
    closeDialog() {
      this.$emit('update:opened', false);
    },
    async reset() {
      if (this.selectedEntityIds.length === 1) {
        const response = await API.company.getNotificationTypeIds(
          this.selectedEntityIds[0],
        );
        this.notificationSubscription = {
          information_related: response.data.includes(1),
          document_related: response.data.includes(2),
          questionnaire_related: response.data.includes(3),
          questionnaire_document_related: response.data.includes(7),
        };
      } else {
        this.notificationSubscription = {
          document_related: false,
          questionnaire_related: false,
          information_related: false,
          questionnaire_document_related: false,
        };
      }
    },
    async saveNotificationSubscription() {
      let notificationTypeIds = [];

      if (this.notificationSubscription.information_related)
        notificationTypeIds.push(1);

      if (this.notificationSubscription.document_related)
        notificationTypeIds.push(2);

      if (this.notificationSubscription.questionnaire_related)
        notificationTypeIds.push(3);

      if (this.notificationSubscription.questionnaire_document_related)
        notificationTypeIds.push(7);

      const response = await API.company.saveNotificationSubscription({
        entityIds: this.selectedEntityIds,
        notificationTypeIds: notificationTypeIds,
      });

      if (response.data.error) {
        console.log(response.data.error);
        this.$store.dispatch('application/setErrorNotification', {
          message: response.data.error,
        });
      } else {
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Successfully saved.',
        });
        this.closeDialog();
      }

      return this.$emit('refresh', notificationTypeIds);
    },
  },
  watch: {
    opened: {
      handler(value) {
        if (value) {
          this.reset();
        }
      },
    },
  },
};
</script>

<style scoped></style>
